import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
  CoreActionResetUserSignature,
  selectorCoreFormCurrentPageStatus,
  selectorCoreUserSignature
} from '@bepatient/core';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { QuestionsV3 } from '../../../classes/enum';
import { SignatureModalComponent } from '../../signature-modal/signature-modal.component';
import { QuestionBaseV3ComponentDirective } from '../question-base.component';

@Component({
  selector: 'bepatient-input-signature-pad-v3',
  templateUrl: './input-signature-pad.component.html',
  styleUrls: ['./input-signature-pad.component.scss'],
  standalone: false
})
export class InputSignaturePadComponentV3
  extends QuestionBaseV3ComponentDirective
  implements OnInit, AfterViewInit, OnDestroy
{
  private modalCtrl = inject(ModalController);
  private store = inject<Store<any>>(Store);

  @Input() question: QuestionsV3;
  @Input() readonly: Boolean;
  @Input() formGroup: UntypedFormGroup;
  @Input() color: string;

  @ViewChild('signatureInput', { read: ElementRef, static: true })
  private signatureElement: ElementRef;
  private subscription: Subscription;

  dirty: boolean;
  fieldErrors: Array<string>;
  isBlur = true;
  signature: string;
  blankSignature: boolean = true;
  hasError: boolean;

  get value(): string {
    if (this.signature) {
      return this.formGroup.get(this.question.key).value;
    }
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.checkFieldError();
  }

  onChange(): void {
    this.isBlur = false;
    this.dirty = this.formGroup.get(this.question.key).dirty;
    this.checkFieldError();
  }

  ngDoCheck(): void {
    this.dirty = this.formGroup.get(this.question.key).dirty;
  }

  /**
   * Method: To get the signature from store
   */
  getSignature(): void {
    // Marking signature field as dirty in any condition
    this.formGroup.get(this.question.key).markAsDirty();
    this.subscription = this.store
      .select(selectorCoreUserSignature)
      .subscribe(signature => {
        if (signature) {
          this.signature = signature;
          // If signature found, Setting signature value into field value
          this.formGroup.get(this.question.key).setValue(signature);
          this.blankSignature = false;
        } else {
          // If no signature, reset signature value & set hasError true
          this.hasError = true;
          this.store.dispatch(new CoreActionResetUserSignature());
        }
        // Marking signature field as touched in any condition
        this.formGroup.get(this.question.key).markAsTouched();
      });
  }

  /**
   * Method: To modify canvas
   * Open modal to change the previous signature
   */
  modify(): void {
    this.presentModal();
  }

  /**
   * Method: To delete signature from store and set blank signature true
   */
  delete(): void {
    this.formGroup.get(this.question.key).patchValue(void 0);
    this.store.dispatch(new CoreActionResetUserSignature());
    this.blankSignature = true;
    this.checkFieldError();
  }

  /**
   * Method: To create dynamic modal component with signature pad
   * */
  async presentModal() {
    const modal = await this.modalCtrl.create({
      component: SignatureModalComponent,
      animated: true,
      mode: 'ios',
      showBackdrop: true,
      id: 'signature-modal',
      componentProps: {
        color: '#2e2e2e'
      },
      cssClass: 'bepatient-custom-signature-modal'
    });
    await modal.present();
    // If modal get dismissed get the signature value and check field background color
    modal.onDidDismiss().then(() => {
      this.getSignature();
      this.checkFieldError();
    });
  }

  // Check if value is empty or not
  isEmpty(): boolean {
    return !this.value;
  }

  /**
   * Check control errors and set the field background
   */
  checkFieldError(): void {
    let controlErrors = this.formGroup.get(this.question.key).errors;
    // Singature field will not have value when closed without sign, so checked set dirty to set the hasError
    this.hasError =
      controlErrors &&
      (this.dirty || this.formGroup.get(this.question.key).touched)
        ? true
        : false;
    this.itemBackgroundColor();
  }

  /**
   * Method: To change signature field background
   */
  itemBackgroundColor(): void {
    if (this.hasError || this.fieldErrors) {
      this.signatureElement.nativeElement.style.setProperty(
        'background-color',
        'rgba(var(--ion-color-danger-rgb), 0.1)'
      );
      // fieldErrors can have always error so testing isEmpty for another validation
      if (!this.isEmpty()) {
        this.signatureElement.nativeElement.style.setProperty(
          'background-color',
          'var(--ion-item-background-color)'
        );
      }
    } else {
      // If no any error found and value has been set setting light background
      if (!this.isEmpty()) {
        this.signatureElement.nativeElement.style.setProperty(
          'background-color',
          'var(--ion-item-background-color)'
        );
      } else {
        this.signatureElement.nativeElement.style.setProperty(
          'background-color',
          'var(--ion-item-background-color)'
        );
      }
    }
  }

  /**
   * On destroy,
   * Reset signature value from store
   * Unsubscribe all subscription
   */
  ngOnDestroy(): void {
    this.store.dispatch(new CoreActionResetUserSignature());
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
